import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Card } from '../components/shared';
import { api } from '../lib/api';
import { getApiErrorMessage } from '../lib/errors';
import { useAuthStore } from '../stores';
import i18n from '../i18n';

type Phase = 'loading' | 'success' | 'error' | 'missing';

/**
 * Landing page for the verification link in the welcome email
 * (`/verify-email?token=…`). Posts the token to the backend, updates the
 * cached user, and shows a clear success/failure message.
 */
export const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const updateUser = useAuthStore((s) => s.updateUser);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [phase, setPhase] = useState<Phase>(token ? 'loading' : 'missing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev — only call the endpoint once,
    // otherwise the second call sees the (now-consumed) token as invalid.
    if (ran.current || !token) return;
    ran.current = true;

    (async () => {
      try {
        await api.verifyEmail(token);
        updateUser({ email_verified: true });
        setPhase('success');
      } catch (err: unknown) {
        setErrorMessage(
          getApiErrorMessage(err, i18n.t('auth.verifyEmail.invalidLink')),
        );
        setPhase('error');
      }
    })();
  }, [token, updateUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-ivory flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <div className="text-4xl mb-2">🐾</div>
        <h1 className="text-2xl font-bold text-gray-900">{t('auth.brand.name')}</h1>

        {phase === 'loading' && (
          <p className="text-sm text-gray-600 mt-6">{t('auth.verifyEmail.confirming')}</p>
        )}

        {phase === 'success' && (
          <>
            <h2 className="text-lg font-semibold text-sage-700 mt-6">{t('auth.verifyEmail.successHeading')}</h2>
            <p className="text-sm text-gray-600 mt-2">
              {t('auth.verifyEmail.successBody')}
            </p>
            <div className="mt-6">
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
              >
                {isAuthenticated ? t('auth.verifyEmail.continueToDashboard') : t('auth.verifyEmail.goToLogin')}
              </Button>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <h2 className="text-lg font-semibold text-red-700 mt-6">{t('auth.verifyEmail.failedHeading')}</h2>
            <p className="text-sm text-gray-700 mt-2">{errorMessage}</p>
            <p className="text-xs text-gray-500 mt-3">
              <Trans
                i18nKey="auth.verifyEmail.failedHint"
                components={{ highlight: <span className="font-medium" /> }}
              />
            </p>
            <div className="mt-6">
              <Link to={isAuthenticated ? '/dashboard' : '/login'}>
                <Button variant="secondary" fullWidth>
                  {isAuthenticated ? t('auth.verifyEmail.backToDashboard') : t('auth.verifyEmail.backToLogin')}
                </Button>
              </Link>
            </div>
          </>
        )}

        {phase === 'missing' && (
          <>
            <h2 className="text-lg font-semibold text-red-700 mt-6">{t('auth.verifyEmail.missingTokenHeading')}</h2>
            <p className="text-sm text-gray-700 mt-2">
              {t('auth.verifyEmail.missingTokenBody')}
            </p>
          </>
        )}
      </Card>
    </div>
  );
};
